import { useEffect, useMemo, useState } from 'react';
import { BookOpen, ChevronDown, ChevronRight, Plus, Sparkles } from 'lucide-react';
import { Button, Card, Input, Modal, Pill, useToast } from '@nako/ui';
import { formatMoney } from '@nako/core';
import { useAuthStore } from '@/stores/auth';
import { useRegionStore } from '@/stores/region';
import { supabase } from '@/lib/supabase';
import { localYmd } from '@/lib/journal-postings';
import { suggestJournalEntry, isGeminiAvailable } from '@/lib/gemini-client';

interface Account {
  id: string;
  gl_code: string;
  name: string;
  type: string;
}

interface JournalEntry {
  id: string;
  business_id: string;
  entry_date: string;
  reference: string | null;
  description: string | null;
  source: string;
  is_posted: boolean;
  is_locked: boolean;
}

interface JournalLine {
  id: string;
  entry_id: string;
  account_id: string;
  debit: number;
  credit: number;
  description: string | null;
}

const SOURCE_TONE: Record<string, 'green' | 'red' | 'blue' | 'accent' | 'neutral' | 'ink'> = {
  sale: 'green',
  purchase: 'blue',
  receipt: 'accent',
  manual: 'neutral',
  payroll: 'ink',
  bank: 'blue',
  adjustment: 'red',
};

export default function Journal() {
  const business = useAuthStore((s) => s.business)!;
  const session = useAuthStore((s) => s.session);
  const regionCode = useRegionStore((s) => s.code);
  const toast = useToast();
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [lines, setLines] = useState<JournalLine[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState<'all' | string>('all');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    date: localYmd(),
    description: '',
    drAccount: '',
    crAccount: '',
    amount: 0,
  });
  const [aiBusy, setAiBusy] = useState(false);
  const [aiNote, setAiNote] = useState<string | null>(null);

  async function aiSuggest() {
    if (!form.description.trim()) {
      toast.show('Type a description first, then I can suggest accounts.', { tone: 'warning' });
      return;
    }
    setAiBusy(true);
    setAiNote(null);
    const suggestion = await suggestJournalEntry({
      description: form.description,
      accounts: accounts.map((a) => ({ gl_code: a.gl_code, name: a.name, type: a.type })),
      industry: business.industry,
    });
    setAiBusy(false);
    if (!suggestion) {
      toast.show('AI could not find a confident match — pick manually.', { tone: 'warning' });
      return;
    }
    const dr = accounts.find((a) => a.gl_code === suggestion.debit_account);
    const cr = accounts.find((a) => a.gl_code === suggestion.credit_account);
    if (!dr || !cr) {
      toast.show('AI suggested unknown accounts — pick manually.', { tone: 'warning' });
      return;
    }
    setForm({ ...form, drAccount: dr.id, crAccount: cr.id });
    setAiNote(
      `${dr.gl_code} ${dr.name} ↔ ${cr.gl_code} ${cr.name}${suggestion.reasoning ? ` — ${suggestion.reasoning}` : ''}`,
    );
    toast.show('AI suggested accounts. Review before posting.', { tone: 'success' });
  }

  useEffect(() => {
    void load();
  }, [business.id]);

  async function load() {
    // Fetch entries first so lines can be filtered by entry_id — defence in
    // depth: RLS scopes this server-side, but the explicit filter prevents a
    // single misconfigured policy from leaking another tenant's ledger
    // (mirrors the Dashboard loader).
    const [{ data: e }, { data: a }] = await Promise.all([
      supabase
        .from('journal_entries')
        .select('*')
        .eq('business_id', business.id)
        .order('entry_date', { ascending: false }),
      supabase.from('accounts').select('*').eq('business_id', business.id).order('gl_code'),
    ]);
    const entryIds = (e ?? []).map((x) => x.id);
    const { data: l } =
      entryIds.length === 0
        ? { data: [] }
        : await supabase.from('journal_lines').select('*').in('entry_id', entryIds);
    setEntries((e ?? []) as JournalEntry[]);
    setLines((l ?? []) as JournalLine[]);
    setAccounts((a ?? []) as Account[]);
  }

  function toggle(id: string) {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpanded(next);
  }

  function accountFor(id: string): Account | undefined {
    return accounts.find((a) => a.id === id);
  }

  async function create() {
    if (!form.drAccount || !form.crAccount || !form.amount || !form.description) {
      toast.show('Description, both accounts and amount required.', { tone: 'warning' });
      return;
    }
    if (form.amount <= 0) {
      toast.show('Amount must be greater than zero.', { tone: 'warning' });
      return;
    }
    if (form.drAccount === form.crAccount) {
      toast.show('Debit and credit accounts must differ.', { tone: 'warning' });
      return;
    }
    const { data: entry } = await supabase
      .from('journal_entries')
      .insert({
        business_id: business.id,
        entry_date: form.date,
        reference: form.description.slice(0, 64),
        description: form.description,
        source: 'manual',
        is_posted: true,
        posted_at: new Date().toISOString(),
        created_by: session?.user.id ?? null,
      })
      .select()
      .single();
    if (!entry) {
      toast.show('Failed to create entry', { tone: 'error' });
      return;
    }
    const { error: linesErr } = await supabase.from('journal_lines').insert([
      {
        entry_id: (entry as { id: string }).id,
        account_id: form.drAccount,
        debit: form.amount,
        credit: 0,
        vat_amount: 0,
        vat_rate: 0,
        description: form.description,
      },
      {
        entry_id: (entry as { id: string }).id,
        account_id: form.crAccount,
        debit: 0,
        credit: form.amount,
        vat_amount: 0,
        vat_rate: 0,
        description: form.description,
      },
    ]);
    // Lines failed but the header committed (separate request) — delete the
    // orphan so it can't corrupt the trial balance (Round-2 finding #1).
    if (linesErr) {
      await supabase
        .from('journal_entries')
        .delete()
        .eq('id', (entry as { id: string }).id);
      toast.show(`Could not post the entry lines: ${linesErr.message}`, { tone: 'error' });
      return;
    }
    toast.show('Manual entry posted.', { tone: 'success' });
    setOpen(false);
    setForm({
      date: localYmd(),
      description: '',
      drAccount: '',
      crAccount: '',
      amount: 0,
    });
    void load();
  }

  const filtered = useMemo(() => {
    if (filter === 'all') return entries;
    return entries.filter((e) => e.source === filter);
  }, [entries, filter]);

  const sources = useMemo(() => {
    const s = new Set<string>();
    for (const e of entries) s.add(e.source);
    return Array.from(s);
  }, [entries]);

  return (
    <main className="mx-auto max-w-3xl px-4 py-4 md:px-6 md:py-6">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-display flex items-center gap-2 text-2xl">
          <BookOpen size={22} /> Journal Entries
        </h1>
        <Button size="sm" leftIcon={<Plus size={16} />} onClick={() => setOpen(true)}>
          Manual entry
        </Button>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        <button
          onClick={() => setFilter('all')}
          className={`rounded-pill border px-3 py-1.5 text-xs font-medium ${
            filter === 'all' ? 'bg-ink text-paper border-ink' : 'border-line text-muted bg-white'
          }`}
        >
          All ({entries.length})
        </button>
        {sources.map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`rounded-pill border px-3 py-1.5 text-xs font-medium ${
              filter === s ? 'bg-ink text-paper border-ink' : 'border-line text-muted bg-white'
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <Card className="py-10 text-center">
          <BookOpen size={32} className="text-muted mx-auto mb-3 opacity-50" />
          <p className="text-muted">No entries match the filter.</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((entry) => {
            const myLines = lines.filter((l) => l.entry_id === entry.id);
            const totalDebit = myLines.reduce((s, l) => s + l.debit, 0);
            const isOpen = expanded.has(entry.id);
            return (
              <Card key={entry.id} compact>
                <button
                  className="flex w-full items-start justify-between gap-3 text-left"
                  onClick={() => toggle(entry.id)}
                >
                  <div className="flex min-w-0 flex-1 items-start gap-2">
                    {isOpen ? (
                      <ChevronDown size={16} className="text-muted mt-0.5 shrink-0" />
                    ) : (
                      <ChevronRight size={16} className="text-muted mt-0.5 shrink-0" />
                    )}
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="truncate text-sm font-medium">
                          {entry.description ?? entry.reference ?? 'Entry'}
                        </p>
                        <Pill tone={SOURCE_TONE[entry.source] ?? 'neutral'}>{entry.source}</Pill>
                      </div>
                      <p className="text-muted text-xs">
                        {entry.entry_date} · {myLines.length} line{myLines.length === 1 ? '' : 's'}
                        {entry.is_locked && ' · locked'}
                      </p>
                    </div>
                  </div>
                  <p className="tnum font-mono text-sm">{formatMoney(totalDebit, regionCode)}</p>
                </button>

                {isOpen && (
                  <table className="border-line mt-3 w-full border-t text-sm">
                    <thead>
                      <tr className="text-muted text-xs">
                        <th className="py-1 text-left">Account</th>
                        <th className="text-right">Dr</th>
                        <th className="text-right">Cr</th>
                      </tr>
                    </thead>
                    <tbody>
                      {myLines.map((l) => {
                        const a = accountFor(l.account_id);
                        return (
                          <tr key={l.id} className="border-line border-t">
                            <td className="py-1">
                              <span className="font-mono text-xs">{a?.gl_code ?? '?'}</span>{' '}
                              {a?.name ?? '(unknown account)'}
                            </td>
                            <td className="tnum text-right font-mono text-xs">
                              {l.debit > 0 ? formatMoney(l.debit, regionCode) : ''}
                            </td>
                            <td className="tnum text-right font-mono text-xs">
                              {l.credit > 0 ? formatMoney(l.credit, regionCode) : ''}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* New manual entry */}
      <Modal open={open} onClose={() => setOpen(false)} title="Post manual journal">
        <div className="space-y-3">
          <Input
            label="Date"
            type="date"
            value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
          />
          <Input
            label="Description"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="e.g. Bought ammo from PAAM, paid bank charges, etc."
          />

          {isGeminiAvailable() && (
            <button
              type="button"
              onClick={aiSuggest}
              disabled={aiBusy || !form.description.trim()}
              className="bg-accent/10 hover:bg-accent/20 text-accent rounded-btn flex w-full items-center justify-center gap-2 py-2 text-sm font-medium disabled:opacity-50"
            >
              <Sparkles size={14} />
              {aiBusy ? 'AI is thinking…' : 'Suggest accounts with AI'}
            </button>
          )}

          {aiNote && (
            <div className="bg-accent/10 text-ink rounded-card p-2 text-xs">
              <span className="font-semibold">AI suggestion:</span> {aiNote}
            </div>
          )}

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium">Debit account</label>
            <select
              className="min-h-tap rounded-btn border-line border bg-white px-3"
              value={form.drAccount}
              onChange={(e) => setForm({ ...form, drAccount: e.target.value })}
            >
              <option value="">— select —</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.gl_code} {a.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium">Credit account</label>
            <select
              className="min-h-tap rounded-btn border-line border bg-white px-3"
              value={form.crAccount}
              onChange={(e) => setForm({ ...form, crAccount: e.target.value })}
            >
              <option value="">— select —</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.gl_code} {a.name}
                </option>
              ))}
            </select>
          </div>
          <Input
            label="Amount"
            type="number"
            numeric
            leftAddon="P"
            value={form.amount || ''}
            onChange={(e) => setForm({ ...form, amount: Number(e.target.value) })}
          />
          <Button full onClick={create}>
            Post entry
          </Button>
        </div>
      </Modal>
    </main>
  );
}
